import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/sportsData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-xl font-semibold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-foreground-500">该文章可能已被移除或地址不正确。</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 九游体育`}
        description={article.excerpt}
        keywords={`九游体育,${article.tags.join(',')},${article.category}`}
        canonicalUrl={`https://www.jiuyou-sports.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: '九游体育',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiuyou-sports.com/' },
              { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.jiuyou-sports.com/category/${article.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-12">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 line-clamp-1">{article.title}</span>
            </nav>

            <article>
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.category}
                </span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mt-6 pb-6 border-b border-background-200/70">
                <span className="text-sm text-foreground-500">{article.author}</span>
                <span className="text-sm text-foreground-500">{article.publishDate}</span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.readCount.toLocaleString()} 阅读
                </span>
              </div>

              <div className="mt-8 rounded-xl overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto max-h-[500px] object-cover"
                />
              </div>

              <div
                className="mt-8 prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                    <i className="ri-thumb-up-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                  </button>
                </div>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  返回{article.category}列表 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-14 md:mt-16">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                          {rel.title}
                        </h4>
                        <p className="mt-1 text-xs text-foreground-500">{rel.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}